/**
 * Qサマリーカード（メンバー管理ページ上部）
 * 選択中 Q の月別 実績 / 目標 / 予算 と 目標達成率 / 予算達成率 を表形式で表示する。
 * 個人マイページの四半期 KPI テーブル（MemberQuarterlyKpiTable）の表ロジック・デザインを踏襲し、
 * 実績・目標は戦闘力サマリー (GET /combat-power) のメンバー月次データを OverallChart と
 * 同じ集計（aggregateMembers）でスコープ合算、予算は fibo予算シート (GET /kpi/monthly-budgets) を参照する。
 * スコープは 全体 (PDG+ACE) / PDG / ACE に加え、ACE の 4 サブチーム
 * （airCloset / Platform / Mall / QA）にも対応し、ヘッダ行は各スコープの代表色
 * （全体=インディゴ / PDG・QA=黄 / ACE・airCloset=赤 / Platform=青 / Mall=緑）で塗り分ける。
 * fibo予算は PDG / ACE 粒度しか持たないため、サブチームスコープでは予算・予算達成率列を表示しない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー管理ページの Qサマリーカード（月別 総実績/総目標/総予算 + 目標達成率/予算達成率 + Q合計。全体/PDG/ACE/ACEサブチーム スコープ対応）
 * @graph-connects useMonthlyDevelopPointBudgets [calls] fibo予算 月次開発pt予算フック
 */
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { getCombatScope } from '@cortex/domain-product-innovation-lab-combat-power-scope';
import type { CombatPowerResponse, MonthlyCombatPower } from '../../../types/combat-power.js';
import type { MonthlyDevelopPointBudget } from '../../../types/api.js';
import { useMonthlyDevelopPointBudgets } from '../../../hooks/useKpi.js';
import { combatPowerAchievementBadgeClass } from '../kpi/WeeklyPointTable.js';
import { aggregateMembers } from './BudgetBarChart.js';
import { ACE_SUB_TEAMS } from './AceTeamsChart.js';
import { formatPt } from '../../../lib/format.js';

/** ACE サブチームのスコープキー（AceTeamsChart のチーム定義と同一） @graph-connects none */
export type AceSubTeamScope = (typeof ACE_SUB_TEAMS)[number]['key'];

/** Qサマリーの集計スコープ @graph-connects none */
export type QuarterSummaryScope = 'overall' | 'pdg' | 'ace' | AceSubTeamScope;

/** @graph-connects none */
interface QuarterSummaryCardProps {
  /** 戦闘力サマリーデータ（選択中 Q） */
  data: CombatPowerResponse;
  /** メンバー名（小文字）→ グループ/チーム対応表 */
  memberTeamMap: Map<string, { group: string; team: string }>;
  /** 集計スコープ（全体 / PDG / ACE / ACE サブチーム） */
  scope: QuarterSummaryScope;
}

/** スコープ表示ラベル（overall は i18n の「全体」を使うためここには持たない） @graph-connects none */
const SCOPE_LABELS: Record<Exclude<QuarterSummaryScope, 'overall'>, string> = {
  pdg: 'PDG',
  ace: 'ACE',
  aircloset: 'airCloset',
  platform: 'Platform',
  mall: 'Mall',
  qa: 'QA',
};

/**
 * ヘッダ行の塗り（スコープ代表色）。全体はマイページ四半期 KPI と同じインディゴ、
 * PDG・QA は代表色の黄 (#eab308)、ACE・airCloset は代表色の赤 (#ef4444)、
 * Platform は青 (#3b82f6)、Mall は緑 (#22c55e) のティント（AceTeamTables の代表色と同一）
 * @graph-connects none
 */
const SCOPE_HEADER_ROW_CLASSES: Record<QuarterSummaryScope, string> = {
  overall: 'bg-[#6366F1]/15',
  pdg: 'bg-[#eab308]/25',
  ace: 'bg-[#ef4444]/20',
  aircloset: 'bg-[#ef4444]/20',
  platform: 'bg-[#3b82f6]/20',
  mall: 'bg-[#22c55e]/20',
  qa: 'bg-[#eab308]/25',
};

/** スコープラベル（左上セル）の文字色 @graph-connects none */
const SCOPE_LABEL_TEXT_CLASSES: Record<QuarterSummaryScope, string> = {
  overall: 'text-[#6366F1]',
  pdg: 'text-[#a16207]',
  ace: 'text-[#b91c1c]',
  aircloset: 'text-[#b91c1c]',
  platform: 'text-[#1d4ed8]',
  mall: 'text-[#15803d]',
  qa: 'text-[#a16207]',
};

/**
 * fibo予算（PDG / ACE 粒度）を参照できるスコープか。
 * ACE サブチームは予算の内訳データが存在しないため予算列を出せない
 * @graph-connects none
 */
export function hasQuarterBudget(scope: QuarterSummaryScope): boolean {
  return scope === 'overall' || scope === 'pdg' || scope === 'ace';
}

/**
 * 列ヘッダ（指標名。数値・達成率バッジと同じ右寄せ）。
 * 幅比 5:2 の狭いカードに収めるため padding を詰め、改行させず 1 行で表示する
 * @graph-connects none
 */
const HEADER_CELL_CLASS =
  'py-8 px-1.5 text-right text-[11px] font-semibold whitespace-nowrap leading-tight text-[var(--color-text-secondary)]';
/**
 * 行ヘッダ（月ラベル列）。行を高めにしてカード全体とのバランスを取る。
 * w-px で列幅を内容（月ピル）にぴったり収め、Q合計 の右揃え位置を月ピルの右端に一致させる
 * @graph-connects none
 */
const ROW_LABEL_CLASS = 'py-1.5 px-1.5 w-px text-left whitespace-nowrap';
/** 数値セル（実績・目標・予算。テーブル既定 text-sm より一回り小さい text-xs） @graph-connects none */
const CELL_CLASS =
  'py-1.5 px-1.5 text-xs text-[var(--color-text-primary)] text-right whitespace-nowrap';
/** 月ラベル（行ヘッダのピル） @graph-connects none */
const MONTH_PILL_CLASS =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none';
/** Q合計 行（マイページ四半期 KPI と同じ落ち着いた強調） @graph-connects none */
const TOTAL_ROW_CLASS = 'bg-[var(--color-bg-elevated)] font-bold';
/** @graph-connects none */
const RATE_BADGE_CLASS =
  'inline-flex items-center justify-center font-medium rounded-md px-1.5 py-0.5 text-[11px] leading-none';

/** 月次行 1 件分の集計結果 @graph-connects none */
interface QuarterSummaryRow {
  /** 月ヘッダ表示（例: 2026-4月） */
  label: string;
  isCurrentMonth: boolean;
  isFutureMonth: boolean;
  /** 実績pt（完了 + 処理中）。未来月は null */
  actual: number | null;
  /** 目標pt（Σ稼働日数×期待値。当月は経過稼働日按分後） */
  target: number;
  /** 予算pt（fibo予算シート。該当月が直近2FYマージ結果に無ければ null） */
  budget: number | null;
  /** 目標達成率 (%) */
  targetRate: number | null;
  /** 予算達成率 (%) */
  budgetRate: number | null;
}

/** "YYYY-M月" 形式の月ヘッダをパース @graph-connects none */
function parseMonthHeader(header: string): { year: number; month: number } | null {
  const m = header.match(/^(\d{4})-(\d{1,2})月$/);
  if (!m) return null;
  return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) };
}

/** 達成率 (%) を小数第1位で算出。分母 0 以下・実績 null は null @graph-connects none */
function achievementRate(actual: number | null, denominator: number | null): number | null {
  if (actual === null || denominator === null || denominator <= 0) return null;
  return Math.round((actual / denominator) * 1000) / 10;
}

/** pt 値（カンマ区切り整数）。null は '-' @graph-connects none */
function formatPtValue(value: number | null): string {
  return value === null ? '-' : formatPt(value);
}

/** 達成率バッジ（null は '-'。当月・当Q進行中は処理中スタイル） @graph-connects none */
function RateBadge({ rate, inProgress }: { rate: number | null; inProgress: boolean }) {
  if (rate === null) return <>-</>;
  return (
    <span className={`${RATE_BADGE_CLASS} ${combatPowerAchievementBadgeClass(rate, inProgress)}`}>
      {`${String(rate)}%`}
    </span>
  );
}

/**
 * スコープに応じた月次行データを組み立てる。
 * 実績・目標は aggregateMembers（OverallChart と同一ロジック）、予算は fibo予算の月次値。
 * ACE サブチームスコープは AceTeamTables と同じ member 分類（group=ACE かつ team 一致）で
 * 絞り込み、予算は粒度が無いため常に null。
 * @graph-connects none
 */
export function buildQuarterSummaryRows(
  data: CombatPowerResponse,
  memberTeamMap: Map<string, { group: string; team: string }>,
  scope: QuarterSummaryScope,
  budgets: MonthlyDevelopPointBudget[],
  now: Date = new Date(),
): QuarterSummaryRow[] {
  const scopeOf = (m: MonthlyCombatPower): 'pdg' | 'ace' | null => {
    const info = memberTeamMap.get(m.name.toLowerCase());
    if (!info) return null;
    const s = getCombatScope({ name: m.name, group: info.group, team: info.team });
    return s === 'pdg' || s === 'ace' ? s : null;
  };
  const pdgMembers = data.members.filter((m) => scopeOf(m) === 'pdg');
  const aceMembers = data.members.filter((m) => scopeOf(m) === 'ace');

  const subTeam = ACE_SUB_TEAMS.find((st) => st.key === scope);
  const subTeamMembers = subTeam
    ? data.members.filter((m) => {
        const info = memberTeamMap.get(m.name.toLowerCase());
        return info?.group === 'ACE' && info.team === subTeam.team;
      })
    : [];

  const budgetByMonth = new Map<string, MonthlyDevelopPointBudget>();
  for (const b of budgets) {
    budgetByMonth.set(`${String(b.year)}-${String(b.month)}`, b);
  }

  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;

  return data.monthHeaders.map((header, idx) => {
    const pdgAgg = aggregateMembers(pdgMembers, idx);
    const aceAgg = aggregateMembers(aceMembers, idx);
    const agg =
      subTeam !== undefined
        ? aggregateMembers(subTeamMembers, idx)
        : scope === 'pdg'
          ? pdgAgg
          : scope === 'ace'
            ? aceAgg
            : {
                actual: pdgAgg.actual + aceAgg.actual,
                actualInProgress: pdgAgg.actualInProgress + aceAgg.actualInProgress,
                target: pdgAgg.target + aceAgg.target,
              };

    const parsed = parseMonthHeader(header);
    const isCurrentMonth = parsed !== null && parsed.year === nowYear && parsed.month === nowMonth;
    const isFutureMonth =
      parsed !== null &&
      (parsed.year > nowYear || (parsed.year === nowYear && parsed.month > nowMonth));

    const monthBudget = parsed
      ? (budgetByMonth.get(`${String(parsed.year)}-${String(parsed.month)}`) ?? null)
      : null;
    const budget =
      monthBudget === null || !hasQuarterBudget(scope)
        ? null
        : scope === 'pdg'
          ? monthBudget.pdgDevelopPointTarget
          : scope === 'ace'
            ? monthBudget.aceDevelopPointTarget
            : monthBudget.pdgDevelopPointTarget + monthBudget.aceDevelopPointTarget;

    const actual = isFutureMonth ? null : agg.actual + agg.actualInProgress;

    return {
      label: header,
      isCurrentMonth,
      isFutureMonth,
      actual,
      target: agg.target,
      budget,
      targetRate: achievementRate(actual, agg.target),
      budgetRate: achievementRate(actual, budget),
    };
  });
}

/**
 * メンバー管理ページ上部の Qサマリーカード。
 * 行=選択中 Q の各月 + Q合計、列=実績 / 目標 / 予算 / 目標達成率 / 予算達成率
 * （ACE サブチームスコープは予算データが無いため 実績 / 目標 / 目標達成率 のみ）。
 * @graph-connects useMonthlyDevelopPointBudgets [calls] fibo予算 月次開発pt予算フック
 */
export function QuarterSummaryCard({ data, memberTeamMap, scope }: QuarterSummaryCardProps) {
  const { t } = useTranslation();
  const { data: budgetData } = useMonthlyDevelopPointBudgets();

  const rows = buildQuarterSummaryRows(data, memberTeamMap, scope, budgetData?.budgets ?? []);
  const showBudget = hasQuarterBudget(scope);

  const includesCurrentMonth = rows.some((r) => r.isCurrentMonth);
  const totalActual = rows.some((r) => r.actual !== null)
    ? rows.reduce((s, r) => s + (r.actual ?? 0), 0)
    : null;
  const totalTarget = rows.reduce((s, r) => s + r.target, 0);
  // 一部の月の予算が欠けている場合、合計を出すと達成率が過大評価になるため null にする
  const totalBudget =
    rows.length > 0 && rows.every((r) => r.budget !== null)
      ? rows.reduce((s, r) => s + (r.budget ?? 0), 0)
      : null;

  const valueColumns =
    scope === 'overall'
      ? [
          t('members.quarterlySummary.totalActual'),
          t('members.quarterlySummary.totalTarget'),
          t('members.quarterlySummary.totalBudget'),
        ]
      : [
          t('members.quarterlySummary.actual'),
          t('members.quarterlySummary.target'),
          t('members.quarterlySummary.budget'),
        ];
  const columns = showBudget
    ? [
        ...valueColumns,
        t('members.quarterlySummary.targetAchievementRate'),
        t('members.quarterlySummary.budgetAchievementRate'),
      ]
    : [valueColumns[0], valueColumns[1], t('members.quarterlySummary.targetAchievementRate')];

  const scopeLabel = scope === 'overall' ? t('kpi.scope.all') : SCOPE_LABELS[scope];

  return (
    // テーブル自体をカードにする: padding なしの Card にテーブルを縁まで敷き、
    // スコープ代表色の帯（タイトル行）をカード上端の角丸ごと見せる
    <Card
      padding="none"
      className="animate-content-reveal h-full overflow-hidden flex flex-col"
      data-testid="quarter-summary-card"
    >
      {/* flex-1 + h-full でテーブル行を均等に伸ばし、カード高さいっぱいに敷く */}
      <div className="overflow-x-auto flex-1">
        <table className="w-full h-full text-sm border-collapse">
          <thead>
            {/* タイトル帯（スコープ代表色）。
                h-full ストレッチの余剰高さはブラウザが tbody 行のみに配分し thead は自然高のままなので、
                列ヘッダの太さは padding（py-8）で確保し、月行は薄め（py-1.5 + ストレッチ分）にして
                ヘッダ ≈ 月行の約2倍の読みやすい比率にする */}
            <tr className={SCOPE_HEADER_ROW_CLASSES[scope]}>
              <th
                colSpan={columns.length + 1}
                className={`py-3.5 px-3 text-left text-[13px] font-bold tracking-wide whitespace-nowrap ${SCOPE_LABEL_TEXT_CLASSES[scope]}`}
              >
                {`${t('members.quarterlySummary.title')}（${scopeLabel}）`}
              </th>
            </tr>
            <tr className="border-b border-[var(--color-border)]">
              <th className={`${ROW_LABEL_CLASS} pl-3`} />
              {columns.map((col, i) => (
                <th
                  key={col}
                  className={`${HEADER_CELL_CLASS} ${i === columns.length - 1 ? 'pr-3' : ''}`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.label}
                className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
              >
                <th scope="row" className={`${ROW_LABEL_CLASS} pl-3`}>
                  <span
                    className={`${MONTH_PILL_CLASS} ${
                      row.isCurrentMonth
                        ? 'bg-[var(--color-accent-primary)] text-white'
                        : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]'
                    }`}
                  >
                    {row.label}
                  </span>
                </th>
                <td className={CELL_CLASS}>{formatPtValue(row.actual)}</td>
                <td className={CELL_CLASS}>{formatPtValue(row.target)}</td>
                {showBudget && <td className={CELL_CLASS}>{formatPtValue(row.budget)}</td>}
                <td className={`${CELL_CLASS} ${showBudget ? '' : 'pr-3'}`}>
                  <RateBadge rate={row.targetRate} inProgress={row.isCurrentMonth} />
                </td>
                {showBudget && (
                  <td className={`${CELL_CLASS} pr-3`}>
                    <RateBadge rate={row.budgetRate} inProgress={row.isCurrentMonth} />
                  </td>
                )}
              </tr>
            ))}
            <tr className={TOTAL_ROW_CLASS}>
              {/* Q合計 ラベルは月ピル列（w-px で列幅=月ピル幅）の中で中央揃え
                  （ROW_LABEL_CLASS は text-left のため使わない） */}
              <th
                scope="row"
                className="py-1.5 px-1.5 pl-3 w-px text-center whitespace-nowrap text-[11px] font-bold text-[var(--color-text-primary)]"
              >
                {t('members.quarterlySummary.qTotal')}
              </th>
              <td className={`${CELL_CLASS} font-bold`}>{formatPtValue(totalActual)}</td>
              <td className={`${CELL_CLASS} font-bold`}>{formatPtValue(totalTarget)}</td>
              {showBudget && (
                <td className={`${CELL_CLASS} font-bold`}>{formatPtValue(totalBudget)}</td>
              )}
              <td className={`${CELL_CLASS} font-bold ${showBudget ? '' : 'pr-3'}`}>
                <RateBadge
                  rate={achievementRate(totalActual, totalTarget)}
                  inProgress={includesCurrentMonth}
                />
              </td>
              {showBudget && (
                <td className={`${CELL_CLASS} pr-3 font-bold`}>
                  <RateBadge
                    rate={achievementRate(totalActual, totalBudget)}
                    inProgress={includesCurrentMonth}
                  />
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      {/* カード下端の脚注（mt-auto で常にカード底辺に寄せる） */}
      <p className="mt-auto px-3 py-2.5 text-xs text-[var(--color-text-muted)]">
        {t('members.quarterlySummary.note')}
      </p>
    </Card>
  );
}
